"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { Scenario } from "@/content/use-cases/types";
import { AppRow, BashMark, logo } from "./ui";
import { cn } from "@/lib/utils";
import { UseCaseCheckIcon } from "@/components/icons/UseCaseCheckIcon";

const DUR = 7500;
const STEP_MS = 620;

type StepState = "idle" | "run" | "did";

/**
 * "Watch it work" — the scenario picker + the live task plate. Ported from
 * the approved mockup: steps light up sequentially, the
 * result card settles, then the next scenario takes the plate. Pauses out of
 * view; a click jumps. SSR renders scenario 0 fully completed so the section
 * reads without JavaScript.
 */
export default function WorkPlate({ scenarios }: { scenarios: Scenario[] }) {
  const [scene, setScene] = useState(0);
  const [run, setRun] = useState(0);
  // no IntersectionObserver (old browser, jsdom) → treat the plate as always
  // in view; the initializer only runs client-side and gates effects, not DOM
  const [visible, setVisible] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof IntersectionObserver === "undefined"
  );
  const s = scenarios[scene];

  // SSR/no-JS initial state: scenario 0, finished.
  const [steps, setSteps] = useState<StepState[]>(() =>
    scenarios[0].steps.map(() => "did")
  );
  const [out, setOut] = useState(true);

  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), {
      threshold: 0.25,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const sc = scenarios[scene];
    const timers: number[] = [];
    if (reduced) {
      // settle on the finished frame — scheduled, so the effect never sets
      // state synchronously (react-hooks/set-state-in-effect)
      timers.push(
        window.setTimeout(() => {
          setSteps(sc.steps.map(() => "did"));
          setOut(true);
        }, 0)
      );
      return () => timers.forEach(clearTimeout);
    }
    timers.push(
      window.setTimeout(() => {
        setSteps(sc.steps.map(() => "idle"));
        setOut(false);
      }, 0)
    );
    let t = 120;
    sc.steps.forEach((_, k) => {
      timers.push(
        window.setTimeout(
          () => setSteps((p) => p.map((v, i) => (i === k ? "run" : v))),
          t
        )
      );
      t += STEP_MS;
      timers.push(
        window.setTimeout(
          () => setSteps((p) => p.map((v, i) => (i === k ? "did" : v))),
          t - 40
        )
      );
    });
    timers.push(window.setTimeout(() => setOut(true), t + 120));
    timers.push(
      window.setTimeout(() => setScene((c) => (c + 1) % scenarios.length), DUR)
    );
    return () => timers.forEach(clearTimeout);
    // `run` retriggers the same scene on click
  }, [scene, run, visible, reduced, scenarios]);

  // On mobile the rail is a horizontal chip strip — keep the live chip in view
  // as autoplay advances. Scrolls the LIST only, never the page; a no-op on
  // desktop where the list doesn't overflow.
  useEffect(() => {
    const list = rootRef.current?.querySelector<HTMLElement>(".room-list");
    const on = list?.querySelector<HTMLElement>(".room-row.is-on");
    if (!list || !on || list.scrollWidth <= list.clientWidth + 8) return;
    const lr = list.getBoundingClientRect();
    const rr = on.getBoundingClientRect();
    list.scrollTo({
      left: list.scrollLeft + (rr.left - lr.left) - (lr.width - rr.width) / 2,
      behavior: reduced ? "auto" : "smooth",
    });
  }, [scene, reduced]);

  const pick = (i: number) => {
    if (i === scene) setRun((r) => r + 1);
    else setScene(i);
  };

  const status = out ? (s.out.held ? "Held" : "Done") : "Running";

  return (
    <div className="work-grid rev in" ref={rootRef}>
      <div className="room-list" role="tablist" aria-label="Scenarios">
        {scenarios.map((sc, i) => (
          <button
            key={sc.row.title}
            type="button"
            role="tab"
            aria-selected={i === scene}
            className={cn("room-row", i === scene ? " is-on" : "")}
            onClick={() => pick(i)}
          >
            <span className="r-i">{String(i + 1).padStart(2, "0")}</span>
            <span className="r-txt">
              <span className="r-n">{sc.row.title}</span>
              <span className="r-s">{sc.row.sub}</span>
            </span>
            <AppRow apps={sc.row.apps} />
            {i === scene && !reduced ? (
              <i
                className="r-clock"
                key={`${scene}-${run}-${visible}`}
                style={{ ["--dur" as string]: `${DUR}ms` }}
              />
            ) : null}
          </button>
        ))}
      </div>
      <div className="work-plate" aria-live="polite">
        <div className="wp-bar">
          <BashMark />
          <b>Bash</b>
          <span className={cn("wp-st", out && s.out.done ? " done" : "")}>
            {status}
          </span>
        </div>
        <div className="wp-body">
          <div className="wp-ask">
            <span className="src">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={logo(s.src.app)} alt="" />
              {s.src.name}
            </span>
            <span className="ask-t">{s.ask}</span>
            <span className="key">
              <b>↵</b>Delegate
            </span>
          </div>
          <div className="wp-plan">
            {s.steps.map((st, k) => (
              <div
                key={st.text}
                className={`wp-step ${steps[k] !== "idle" ? " on" : ""} ${
                  steps[k]
                }`.trimEnd()}
              >
                <span className="app">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={logo(st.app)} alt="" />
                </span>
                <span className="txt">{st.text}</span>
                <span className="st">
                  <UseCaseCheckIcon />
                </span>
              </div>
            ))}
          </div>
          <div className={cn("wp-out", out ? " on" : "")}>
            <div className="cap">{s.out.cap}</div>
            <div className="ttl">{s.out.title}</div>
            <div className="q">{s.out.quote}</div>
            <div className="foot">
              <span className={cn("pill", s.out.done ? " done" : "")}>
                {s.out.pill}
              </span>
              {s.out.held ? (
                <span className="held-btns">
                  <span>Edit</span>
                  <span>Hold</span>
                  <span className="ok">Approve all</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
